import styles from './ShiftReport.module.css';

import { useState, useEffect, useCallback } from 'react';
import { DateTime } from 'luxon';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import Air from '@mui/icons-material/Air';
import AcUnit from '@mui/icons-material/AcUnit';
import Cancel from '@mui/icons-material/Cancel';
import CloudySnowing from '@mui/icons-material/CloudySnowing';
import DeviceThermostat from '@mui/icons-material/DeviceThermostat';
import Edit from '@mui/icons-material/Edit';
import Foggy from '@mui/icons-material/Foggy';
import Refresh from '@mui/icons-material/Refresh';
import SevereCold from '@mui/icons-material/SevereCold';
import Save from '@mui/icons-material/Save';
import Thunderstorm from '@mui/icons-material/Thunderstorm';
import WbSunny from '@mui/icons-material/WbSunny';
import WbCloudy from '@mui/icons-material/WbCloudy';

import * as api from '../../api';

export const ShiftReport = ({ patrollerId, onSave, initialData = null }) => {
    const [ isEditing, setIsEditing ] = useState(!initialData);
    const [ isSaving, setIsSaving ] = useState(false);
    const [ error, setError ] = useState(null);
    const [ weatherLoading, setWeatherLoading ] = useState(false);
    const [ nearbyStations, setNearbyStations ] = useState([]);
    const [ reportData, setReportData ] = useState(() => {
        const baseData = {
            patrollerId: patrollerId || '',
            rptDate: DateTime.now().toISODate(),
            rptTime: DateTime.now().toFormat('HH:mm'),
            temp: '',
            wind: '',
            windDirection: '',
            windGust: '',
            windChill: '',
            precip: '',
            snow: '',
            comments: ''
        };

        if (initialData) {
            return {
                ...baseData,
                ...initialData,
                rptDate: initialData.rptDate || baseData.rptDate,
                rptTime: initialData.rptTime ? DateTime.fromMillis(initialData.rptTime).toFormat('HH:mm') : baseData.rptTime
            };
        }

        return baseData;
    });

    const resortLocation = api.getResortWeatherLocation();

    const fetchCurrentWeather = useCallback(async () => {
        setWeatherLoading(true);
        setError(null);

        try {
            // Weather configuration - NWS doesn't require API key
            const weatherData = await api.getCurrentWeather(
                resortLocation.lat,
                resortLocation.lon
            );

            setReportData(prev => ({
                ...prev,
                temp: weatherData.temperature ? weatherData.temperature.toString() : '',
                precip: weatherData.conditions || 'Unknown',
                wind: weatherData.wind === 'Unknown' && weatherData.wind === 0 ? 'Calm' : weatherData.wind,
                snow: weatherData.snow || 'Unknown',
                windDirection: weatherData.windDirection ? weatherData.windDirection.toString() : ''
            }));

            setNearbyStations(weatherData.nearbyStations || []);
        } catch (err) {
            console.error('Failed to fetch weather:', err);

            setError('Failed to load current weather conditions from National Weather Service');
        } finally {
            setWeatherLoading(false);
        }
    }, [ resortLocation.lat, resortLocation.lon ]);

    // Load weather data on component mount if editing new report
    useEffect(() => {
        if (isEditing && !initialData) {
            fetchCurrentWeather();
        }
    }, [ isEditing, initialData, fetchCurrentWeather ]);

    // Weather condition options
    const weatherConditions = [
        'Unknown',
        'Clear',
        'Partly Cloudy',
        'Overcast',
        'Light Snow',
        'Heavy Snow',
        'Fog',
        'Wind Hold',
        'Ice',
        'Rain'
    ];

    // Wind condition options
    const windConditions = [
        'Unknown',
        'Calm < 4MPH',
        'Light 4-8MPH',
        'Moderate 8-15MPH',
        'Strong 15-25MPH',
        'Extreme > 25MPH'
    ];

    const windDirections = [
        { direction: 'N', start: 349, end: 11 },
        { direction: 'NNE', start: 11, end: 34 },
        { direction: 'NE', start: 34, end: 56 },
        { direction: 'ENE', start: 56, end: 79 },
        { direction: 'E', start: 79, end: 101 },
        { direction: 'ESE', start: 101, end: 124 },
        { direction: 'SE', start: 124, end: 146 },
        { direction: 'SSE', start: 146, end: 169 },
        { direction: 'S', start: 169, end: 191 },
        { direction: 'SSW', start: 191, end: 214 },
        { direction: 'SW', start: 214, end: 236 },
        { direction: 'WSW', start: 236, end: 259 },
        { direction: 'W', start: 259, end: 281 },
        { direction: 'WNW', start: 281, end: 304 },
        { direction: 'NW', start: 304, end: 326 },
        { direction: 'NNW', start: 326, end: 349 }
    ];

    // Snow condition options
    const snowConditions = [
        'Unknown',
        'None',
        'Packed Powder',
        'Powder',
        'Spring',
        'Wet',
        'Icy',
        'Variable',
        'Crust',
        'Other'
    ];

    const getWindDirectionFromDegrees = (degrees) => {
        if (!degrees) return '';

        const deg = parseFloat(degrees);

        for (const dir of windDirections) {
            if (dir.start <= dir.end) {
                if (deg >= dir.start && deg <= dir.end) return dir.direction;
            } else {
                if (deg >= dir.start || deg <= dir.end) return dir.direction;
            }
        }

        return '';
    };

    const handleInputChange = (field, value) => {
        setReportData(prev => ({
            ...prev,
            [ field ]: value
        }));

        setError(null);
    };

    const handleSave = async () => {
        if (!reportData.precip || !reportData.temp || !reportData.wind || !reportData.snow) {
            setError('Weather conditions are required');

            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            const reportToSave = {
                ...reportData,
                rptDate: DateTime.now().toISODate(),
                rptTime: DateTime.fromFormat(reportData.rptTime, 'HH:mm').toMillis(),
            };

            await onSave(reportToSave);

            setIsEditing(false);
        } catch (err) {
            console.error('Failed to save report:', err);

            setError('Failed to save report: ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (initialData) {
            setReportData({ ...initialData });
            setIsEditing(false);
        }
        setError(null);
    };

    return (
        <Card className={ styles.reportCard }>
            <CardHeader
                title={
                    <Box display="flex" alignItems="center" gap={ 1 }>
                        <WbSunny />
                        <Typography variant="h6">
                            Shift Report - { DateTime.fromISO(reportData.rptDate).toLocal().toFormat('DDD') }
                        </Typography>
                        { weatherLoading && <CircularProgress size={ 20 } /> }
                    </Box>
                }
                action={
                    <Box display="flex" gap={ 1 }>
                        <Tooltip title="Refresh current weather from National Weather Service">
                            <IconButton
                                onClick={ fetchCurrentWeather }
                                disabled={ weatherLoading }
                                size="small"
                            >
                                <Refresh />
                            </IconButton>
                        </Tooltip>

                        { !isEditing && (
                            <Button
                                startIcon={ <Edit /> }
                                onClick={ () => setIsEditing(true) }
                                size="small"
                            >
                                Edit
                            </Button>
                        ) }
                    </Box>
                }
            />

            <CardContent>
                { error && (
                    <Alert variant='filled' severity="error" className={ styles.errorAlert }>
                        { error }
                    </Alert>
                ) }

                <Grid container spacing={ 3 }>
                    <Grid size={ { xs: 12, md: 6 } } gap={ 2 } container>
                        <TextField
                            label="Report Date"
                            type="date"
                            fullWidth
                            value={ reportData.rptDate }
                            onChange={ (e) => handleInputChange('rptDate', e.target.value) }
                            disabled={ !isEditing }
                            slotProps={ { inputLabel: { shrink: true } } }
                            size="medium"
                            sx={ { mb: 1 } }
                        />

                        <TextField
                            label="Report Time"
                            type="time"
                            fullWidth
                            value={ reportData.rptTime }
                            onChange={ (e) => handleInputChange('rptTime', e.target.value) }
                            disabled={ !isEditing }
                            slotProps={ { inputLabel: { shrink: true } } }
                            size="medium"
                            sx={ { mb: 1 } }
                        />

                        <FormControl fullWidth disabled={ !isEditing } size="medium">
                            <InputLabel id="sky-precip-label">Sky/Precip</InputLabel>
                            <Select
                                labelId="sky-precip-label"
                                value={ reportData.precip }
                                label="Sky/Precip"
                                onChange={ (e) => handleInputChange('precip', e.target.value) }
                                MenuProps={ { disableScrollLock: true } }
                                sx={ { mb: 1 } }
                            >
                                { weatherConditions.map((condition) => (
                                    <MenuItem key={ condition } value={ condition }>
                                        { condition }
                                    </MenuItem>
                                )) }
                            </Select>
                        </FormControl>

                        <FormControl fullWidth disabled={ !isEditing } size="medium">
                            <InputLabel id="wind-label">Wind</InputLabel>
                            <Select
                                labelId="wind-label"
                                value={ reportData.wind }
                                label="Wind Speed"
                                onChange={ (e) => handleInputChange('wind', e.target.value) }
                                MenuProps={ { disableScrollLock: true } }
                                sx={ { mb: 1 } }
                            >
                                { windConditions.map((wind) => (
                                    <MenuItem key={ wind } value={ wind }>
                                        { wind }
                                    </MenuItem>
                                )) }
                            </Select>
                        </FormControl>

                        <FormControl fullWidth disabled={ !isEditing } size="medium">
                            <InputLabel id="snow-label">Snow</InputLabel>
                            <Select
                                labelId="snow-label"
                                value={ reportData.snow }
                                label="Snow"
                                onChange={ (e) => handleInputChange('snow', e.target.value) }
                                MenuProps={ { disableScrollLock: true } }
                                sx={ { mb: 1 } }
                            >
                                { snowConditions.map((snow) => (
                                    <MenuItem key={ snow } value={ snow }>
                                        { snow }
                                    </MenuItem>
                                )) }
                            </Select>
                        </FormControl>

                        <TextField
                            label="Temperature (°F)"
                            type="number"
                            fullWidth
                            value={ reportData.temp }
                            autoComplete='off'
                            onChange={ (e) => handleInputChange('temp', e.target.value) }
                            disabled={ !isEditing }
                            size="medium"
                            slotProps={ { inputLabel: { shrink: true }, input: { startAdornment: <DeviceThermostat /> } } }
                            sx={ { mb: 1, pt: 1 } }
                        />
                    </Grid>

                    <Grid size={ { xs: 12, md: 6 } } container gap={ 1 }>
                        <TextField
                            label="Comments"
                            fullWidth
                            multiline
                            minRows={ 8 }
                            value={ reportData.comments }
                            onChange={ (e) => handleInputChange('comments', e.target.value) }
                            disabled={ !isEditing }
                            placeholder="Enter any notable events, incidents, or observations for the shift..."
                            size="medium"
                            sx={ { mb: 1 } }
                        />

                        <FormControl fullWidth disabled={ !isEditing } size="medium">
                            <InputLabel id="wind-direction-label">Wind Direction</InputLabel>
                            
                            <Select
                                labelId="wind-direction-label"
                                value={ getWindDirectionFromDegrees(reportData.windDirection) }
                                label="Wind Direction"
                                onChange={ (e) => handleInputChange('windDirection', e.target.value) }
                                MenuProps={ { disableScrollLock: true } }
                                sx={ { mb: 1 } }
                            >
                                { windDirections.map((dir) => (
                                    <MenuItem key={ dir.direction } value={ dir.direction }>
                                        { dir.direction } ({ dir.start }° - { dir.end }°)
                                    </MenuItem>
                                )) }
                            </Select>
                        </FormControl>                        
                        
                        <TextField
                            label="Wind Gust"
                            type="number"
                            fullWidth
                            value={ reportData.windGust }
                            autoComplete='off'
                            onChange={ (e) => handleInputChange('windGust', e.target.value) }
                            disabled={ !isEditing }
                            size="medium"
                            slotProps={ { inputLabel: { shrink: true }, input: { startAdornment: <Air /> } } }
                            sx={ { mb: 1, pt: 1 } }
                        />

                        <TextField
                            label="Wind Chill"
                            type="number"
                            fullWidth
                            value={ reportData.windChill }
                            autoComplete='off'
                            onChange={ (e) => handleInputChange('windChill', e.target.value) }
                            disabled={ !isEditing }
                            size="medium"
                            slotProps={ { inputLabel: { shrink: true }, input: { startAdornment: <AcUnit /> } } }
                            sx={ { mb: 1, pt: 1 } }
                        />
                    </Grid>
                </Grid>

                { nearbyStations.length > 0 && (
                    <Grid container size={ { xs: 12 } } sx={ { mt: 2 } }>
                        <Box sx={ { p: 1, border: '1px solid #eee', borderRadius: 1, width: '100%' } }>
                            <Typography variant="subtitle2" sx={ { mb: 1 } }>
                                Nearby Weather Stations
                            </Typography>

                            { nearbyStations.map((station, idx) => (
                                <Box key={ (station.stationId || idx) + '-container' } sx={ { display: 'grid', gridTemplateColumns: 'auto 45% 15% 1fr', gap: 1 } }>
                                    <Box key={ (station.stationId || idx) + '-icon' } sx={ { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 32 } }>
                                        { station.conditions === 'Clear' && <WbSunny fontSize="small" color="primary" /> }
                                        { station.conditions === 'Partly Cloudy' && <WbCloudy fontSize="small" color="action" /> }
                                        { station.conditions === 'Overcast' && <WbCloudy fontSize="small" color="disabled" /> }
                                        { station.conditions === 'Rain' && <Thunderstorm fontSize="small" color="info" /> }
                                        { station.conditions === 'Light Snow' && <CloudySnowing fontSize="small" color="secondary" /> }
                                        { station.conditions === 'Heavy Snow' && <CloudySnowing fontSize="small" color="secondary" /> }
                                        { station.conditions === 'Fog' && <Foggy fontSize="small" color="disabled" /> }
                                        { station.conditions === 'Ice' && <SevereCold fontSize="small" color="error" /> }
                                        { station.conditions === 'Wind Hold' && <Air fontSize="small" color="warning" /> }
                                    </Box>

                                    <Typography key={ (station.stationId || idx) + '-name' } variant="body2" sx={ { fontWeight: 500, minWidth: 90, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', alignSelf: 'center' } }>{ station.name }</Typography>
                                    <Typography key={ (station.stationId || idx) + '-temp' } variant="body2" sx={ { alignSelf: 'center', textAlign: 'left' } }>Temp: { station.temperature }&deg;F</Typography>
                                    <Typography key={ (station.stationId || idx) + '-wind' } variant="body2" sx={ { alignSelf: 'center', textAlign: 'left' } }>Wind: { station.wind }</Typography>
                                </Box>
                            )) }
                        </Box>
                    </Grid>
                ) }

                {/* Action Buttons */ }
                { isEditing && (
                    <Grid size={ { xs: 12 } }>
                        <Box sx={ { display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 } }>
                            <Button
                                variant="outlined"
                                startIcon={ <Cancel /> }
                                onClick={ handleCancel }
                                disabled={ isSaving }
                                size="medium"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={ <Save /> }
                                onClick={ handleSave }
                                disabled={ isSaving }
                                className={ styles.saveButton }
                                size="medium"
                            >
                                { isSaving ? 'Saving...' : 'Save Report' }
                            </Button>
                        </Box>
                    </Grid>
                ) }
            </CardContent>
        </Card>
    );
};